export interface CityData {
  name: string;
  lat: number;
  lng: number;
}

export interface LocationData {
  slug: string;
  name: string;
  state: string;
  region: string;
  description: string;
  highlights: string[];
  content: string[];
  isPrimary: boolean;
  lat: number;
  lng: number;
  cities: CityData[];
}

const REGION = "Shenandoah Valley";
const STATE = "VA";

const highlights = (name: string): string[] => [
  `Local cleaning crews serving ${name}`,
  "Free, no-pressure estimates",
  "Residential & commercial service",
  "Flexible one-time or recurring visits",
];

const content = (name: string): string[] => [
  `Good Choice Cleaning & Multi Services proudly serves homes and businesses throughout ${name}, ${STATE}. As a locally rooted cleaning company based in the ${REGION}, we bring dependable, detail-focused service to every property we clean — with trained, background-checked crews who treat your space with respect.`,
  `From routine house cleaning and deep cleans to move-in/move-out turnovers, office and janitorial work, and post-construction cleanup, our team in ${name} handles it all. We work around your schedule and build a checklist around your priorities, so you get exactly the clean you're paying for.`,
  `Ready to see the Good Choice difference in ${name}? Request your free estimate today and ask about our referral reward — earn a free regular cleaning for every two successful referrals.`,
];

export const locations: LocationData[] = [
  {
    slug: "harrisonburg",
    name: "Harrisonburg",
    state: STATE,
    region: REGION,
    isPrimary: true,
    lat: 38.4496,
    lng: -78.8689,
    description:
      "Our home base. Good Choice Cleaning delivers trusted residential and commercial cleaning across Harrisonburg and the surrounding Shenandoah Valley.",
    highlights: highlights("Harrisonburg"),
    content: content("Harrisonburg"),
    cities: [
      { name: "Park View", lat: 38.4585, lng: -78.8697 },
      { name: "Cross Keys", lat: 38.3579, lng: -78.8339 },
      { name: "Mount Crawford", lat: 38.3374, lng: -78.9367 },
      { name: "Pleasant Valley", lat: 38.3821, lng: -78.8622 },
      { name: "Keezletown", lat: 38.4382, lng: -78.7714 },
    ],
  },
  {
    slug: "elkton",
    name: "Elkton",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.4093,
    lng: -78.6236,
    description:
      "Reliable home and business cleaning for Elkton and the eastern edge of Rockingham County.",
    highlights: highlights("Elkton"),
    content: content("Elkton"),
    cities: [
      { name: "McGaheysville", lat: 38.4118, lng: -78.6431 },
      { name: "Shenandoah", lat: 38.4857, lng: -78.6222 },
      { name: "Island Ford", lat: 38.3663, lng: -78.6469 },
    ],
  },
  {
    slug: "staunton",
    name: "Staunton",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.1496,
    lng: -79.0717,
    description:
      "Professional cleaning services for historic Staunton — homes, offices, and everything in between.",
    highlights: highlights("Staunton"),
    content: content("Staunton"),
    cities: [
      { name: "Fishersville", lat: 38.0993, lng: -78.9689 },
      { name: "Churchville", lat: 38.2265, lng: -79.1747 },
      { name: "Swoope", lat: 38.1846, lng: -79.1611 },
    ],
  },
  {
    slug: "bridgewater",
    name: "Bridgewater",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.3818,
    lng: -78.9767,
    description:
      "Dependable cleaning for Bridgewater homes, rentals, and small businesses.",
    highlights: highlights("Bridgewater"),
    content: content("Bridgewater"),
    cities: [
      { name: "Mount Crawford", lat: 38.3374, lng: -78.9367 },
      { name: "Dayton", lat: 38.4143, lng: -78.9392 },
      { name: "Spring Creek", lat: 38.3512, lng: -78.9861 },
    ],
  },
  {
    slug: "weyers-cave",
    name: "Weyers Cave",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.2884,
    lng: -78.9139,
    description:
      "Cleaning services you can count on across Weyers Cave and central Augusta County.",
    highlights: highlights("Weyers Cave"),
    content: content("Weyers Cave"),
    cities: [
      { name: "Mount Sidney", lat: 38.2543, lng: -78.9803 },
      { name: "Fort Defiance", lat: 38.2371, lng: -78.9367 },
      { name: "New Hope", lat: 38.2118, lng: -78.8892 },
    ],
  },
  {
    slug: "grottoes",
    name: "Grottoes",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.2687,
    lng: -78.8253,
    description:
      "Trusted residential and commercial cleaning for Grottoes and the Blue Ridge foothills.",
    highlights: highlights("Grottoes"),
    content: content("Grottoes"),
    cities: [
      { name: "Port Republic", lat: 38.3096, lng: -78.8181 },
      { name: "Mount Meridian", lat: 38.2743, lng: -78.8542 },
      { name: "Crimora", lat: 38.1685, lng: -78.8497 },
    ],
  },
  {
    slug: "waynesboro",
    name: "Waynesboro",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.0685,
    lng: -78.8895,
    description:
      "Full-service home and office cleaning for Waynesboro and the eastern Valley.",
    highlights: highlights("Waynesboro"),
    content: content("Waynesboro"),
    cities: [
      { name: "Fishersville", lat: 38.0993, lng: -78.9689 },
      { name: "Stuarts Draft", lat: 38.0246, lng: -79.0272 },
      { name: "Lyndhurst", lat: 38.0293, lng: -78.9581 },
    ],
  },
  {
    slug: "penn-laird",
    name: "Penn Laird",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.3857,
    lng: -78.7886,
    description:
      "Local cleaning crews serving Penn Laird and the communities just east of Harrisonburg.",
    highlights: highlights("Penn Laird"),
    content: content("Penn Laird"),
    cities: [
      { name: "McGaheysville", lat: 38.4118, lng: -78.6431 },
      { name: "Massanetta Springs", lat: 38.4093, lng: -78.8231 },
      { name: "Cross Keys", lat: 38.3579, lng: -78.8339 },
    ],
  },
  {
    slug: "massanutten",
    name: "Massanutten",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.4155,
    lng: -78.7622,
    description:
      "Resort, rental, and home cleaning for the Massanutten area — including fast Airbnb turnovers.",
    highlights: highlights("Massanutten"),
    content: content("Massanutten"),
    cities: [
      { name: "McGaheysville", lat: 38.4118, lng: -78.6431 },
      { name: "Elkton", lat: 38.4093, lng: -78.6236 },
      { name: "Penn Laird", lat: 38.3857, lng: -78.7886 },
    ],
  },
  {
    slug: "broadway",
    name: "Broadway",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.6113,
    lng: -78.7981,
    description:
      "Dependable cleaning for Broadway, Timberville, and the northern Rockingham communities.",
    highlights: highlights("Broadway"),
    content: content("Broadway"),
    cities: [
      { name: "Timberville", lat: 38.6382, lng: -78.7739 },
      { name: "Fulks Run", lat: 38.6396, lng: -78.9236 },
      { name: "Lacey Spring", lat: 38.5257, lng: -78.8506 },
    ],
  },
  {
    slug: "new-market",
    name: "New Market",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.6482,
    lng: -78.6714,
    description:
      "Home and business cleaning for New Market and the upper Shenandoah Valley.",
    highlights: highlights("New Market"),
    content: content("New Market"),
    cities: [
      { name: "Timberville", lat: 38.6382, lng: -78.7739 },
      { name: "Mount Jackson", lat: 38.7457, lng: -78.6425 },
      { name: "Forestville", lat: 38.6693, lng: -78.6842 },
    ],
  },
  {
    slug: "dayton",
    name: "Dayton",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.4143,
    lng: -78.9392,
    description:
      "Friendly, thorough cleaning for Dayton homes and Main Street businesses.",
    highlights: highlights("Dayton"),
    content: content("Dayton"),
    cities: [
      { name: "Bridgewater", lat: 38.3818, lng: -78.9767 },
      { name: "Mount Crawford", lat: 38.3374, lng: -78.9367 },
      { name: "Silver Lake", lat: 38.4257, lng: -78.9439 },
    ],
  },
  {
    slug: "verona",
    name: "Verona",
    state: STATE,
    region: REGION,
    isPrimary: false,
    lat: 38.2043,
    lng: -79.0089,
    description:
      "Reliable residential and commercial cleaning for Verona and central Augusta County.",
    highlights: highlights("Verona"),
    content: content("Verona"),
    cities: [
      { name: "Fort Defiance", lat: 38.2371, lng: -78.9367 },
      { name: "Mount Sidney", lat: 38.2543, lng: -78.9803 },
      { name: "Churchville", lat: 38.2265, lng: -79.1747 },
    ],
  },
];
